import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/pages/portal/components/PortalLayout";
import { useAuth } from "@/contexts/AuthContext";
import {
  getMyListings,
  deleteMyListing,
  type OwnerListing,
  type OwnerListingStatus,
  type ListingUpdatePayload,
} from "@/services/ownerService";
import { ApiError } from "@/services/http";
import ListingEditor from "./components/ListingEditor";

// ── Status presentation ───────────────────────────────────────────────────────

const STATUS_META: Record<OwnerListingStatus, { label: string; icon: string; className: string }> = {
  active:    { label: "Live",           icon: "ri-checkbox-circle-fill", className: "bg-emerald-100 text-emerald-700" },
  pending:   { label: "In review",      icon: "ri-time-line",             className: "bg-amber-100 text-amber-700" },
  rejected:  { label: "Needs changes",  icon: "ri-error-warning-fill",    className: "bg-rose-100 text-rose-700" },
  suspended: { label: "Suspended",      icon: "ri-pause-circle-fill",     className: "bg-background-200 text-foreground-600" },
};

const PLAN_LABEL: Record<string, string> = {
  free:     "Free plan",
  business: "Business plan",
  premium:  "Premium plan",
};

export default function PortalListingsPage() {
  const { accessToken, isDemoMode } = useAuth();
  const [listings, setListings] = useState<OwnerListing[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [editing,  setEditing]  = useState<OwnerListing | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [busyId,   setBusyId]   = useState<number | null>(null);
  const [toast,    setToast]    = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data = await getMyListings(accessToken);
      setListings(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "We could not load your listings. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  function handleSaved(id: number, payload: ListingUpdatePayload) {
    setListings((prev) =>
      prev.map((l) =>
        l.id === id
          ? {
              ...l,
              ...payload,
              // A rejected listing is re-queued for moderation on save.
              status: l.status === "rejected" ? "pending" : l.status,
              rejectionReason: l.status === "rejected" ? null : l.rejectionReason,
              updatedAt: new Date().toISOString(),
            }
          : l,
      ),
    );
    setEditing(null);
    setToast("Listing updated successfully.");
    setTimeout(() => setToast(""), 3200);
  }

  async function handleDelete(id: number) {
    setBusyId(id);
    try {
      await deleteMyListing(accessToken, id);
      setListings((prev) => prev.filter((l) => l.id !== id));
      setToast("Listing withdrawn.");
      setTimeout(() => setToast(""), 3200);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not remove the listing. Please try again.");
    } finally {
      setBusyId(null);
      setConfirmId(null);
    }
  }

  const total    = listings.length;
  const live     = listings.filter((l) => l.status === "active").length;
  const review   = listings.filter((l) => l.status === "pending").length;
  const attention = listings.filter((l) => l.status === "rejected").length;

  const stats = [
    { label: "Total listings", value: total,     icon: "ri-layout-grid-line",     color: "text-primary-600", bg: "bg-primary-50" },
    { label: "Live",           value: live,      icon: "ri-checkbox-circle-line", color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "In review",      value: review,    icon: "ri-time-line",            color: "text-amber-600",   bg: "bg-amber-50" },
    { label: "Needs changes",  value: attention, icon: "ri-error-warning-line",   color: "text-rose-600",    bg: "bg-rose-50" },
  ];

  return (
    <PortalLayout>
      <div className="max-w-4xl">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">My Listings</h1>
            <p className="mt-1 text-sm text-foreground-600">
              Keep your business details fresh — edits to rejected listings are resubmitted for review.
            </p>
          </div>
          <Link
            to="/list-your-business"
            className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-add-line text-base" /> Add business
          </Link>
        </div>

        {/* Stats */}
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="rounded-xl border border-background-200 bg-background-50 p-5">
              <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-lg ${s.bg}`}>
                <i className={`${s.icon} text-lg ${s.color}`} />
              </div>
              <p className="font-heading text-2xl font-bold text-foreground-950">{loading ? "—" : s.value}</p>
              <p className="mt-0.5 text-xs text-foreground-500">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Toast */}
        {toast && (
          <div className="mb-5 flex items-center gap-2.5 rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            <i className="ri-checkbox-circle-line text-base" /> {toast}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-5 flex items-start justify-between gap-3 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <span className="flex items-start gap-2.5">
              <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" />{error}
            </span>
            <button
              type="button"
              onClick={load}
              className="shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100"
            >
              Retry
            </button>
          </div>
        )}

        {/* Listings */}
        <div className="rounded-xl border border-background-200 bg-background-50">
          <div className="flex items-center justify-between border-b border-background-200 px-6 py-4">
            <h2 className="font-heading text-base font-bold text-foreground-950">Your businesses</h2>
            <div className="flex items-center gap-3">
              {isDemoMode && (
                <span className="rounded-full bg-accent-100 px-3 py-1 text-[11px] font-semibold text-accent-700">
                  Demo Mode
                </span>
              )}
              <span className="text-xs text-foreground-500">{listings.length} total</span>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
            </div>
          ) : listings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
                <i className="ri-store-3-line text-2xl text-foreground-400" />
              </div>
              <p className="text-sm font-medium text-foreground-700">No listings yet</p>
              <p className="mt-1 text-xs text-foreground-500">Add your first business to get discovered by customers.</p>
              <Link
                to="/list-your-business"
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-add-line text-base" /> List your business
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-background-100">
              {listings.map((listing) => {
                const meta = STATUS_META[listing.status];
                return (
                  <div key={listing.id} className="px-6 py-5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-start gap-4">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-base font-bold text-primary-700">
                          {listing.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-bold text-foreground-950">{listing.name}</p>
                            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${meta.className}`}>
                              <i className={`${meta.icon} text-[11px]`} /> {meta.label}
                            </span>
                          </div>
                          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-foreground-500">
                            <span>{listing.categoryName}</span>
                            <span aria-hidden="true">·</span>
                            <span>{listing.townName ?? listing.districtName}</span>
                            <span aria-hidden="true">·</span>
                            <span>{PLAN_LABEL[listing.planSlug] ?? listing.planSlug}</span>
                          </p>
                          {listing.status === "active" && (
                            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-foreground-500">
                              <i className="ri-star-fill text-[11px] text-accent-500" />
                              {listing.rating.toFixed(1)} ({listing.reviewCount} reviews)
                            </p>
                          )}
                          {listing.status === "rejected" && listing.rejectionReason && (
                            <p className="mt-2 rounded-md bg-rose-50 px-3 py-2 text-xs leading-relaxed text-rose-700">
                              {listing.rejectionReason}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex shrink-0 flex-wrap gap-2">
                        {listing.status === "active" && (
                          <Link
                            to={`/business/${listing.slug}`}
                            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                          >
                            <i className="ri-external-link-line text-sm" /> View
                          </Link>
                        )}
                        <button
                          type="button"
                          onClick={() => setEditing(listing)}
                          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-100"
                        >
                          <i className="ri-pencil-line text-sm" /> Edit
                        </button>
                        {listing.status !== "active" && (
                          <button
                            type="button"
                            onClick={() => setConfirmId(listing.id)}
                            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs font-semibold text-foreground-600 transition-colors hover:border-rose-300 hover:text-rose-600"
                          >
                            <i className="ri-delete-bin-line text-sm" /> Withdraw
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Inline delete confirmation */}
                    {confirmId === listing.id && (
                      <div className="mt-4 flex flex-col gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-rose-700">
                          Withdraw <span className="font-semibold">{listing.name}</span>? This cannot be undone.
                        </p>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setConfirmId(null)}
                            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            disabled={busyId === listing.id}
                            onClick={() => handleDelete(listing.id)}
                            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-background-50 transition-colors hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {busyId === listing.id
                              ? <><i className="ri-loader-4-line animate-spin text-sm" /> Removing…</>
                              : <><i className="ri-delete-bin-line text-sm" /> Withdraw</>}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <ListingEditor
          listing={editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
    </PortalLayout>
  );
}